import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../../../firebase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getUserWithClaims = useCallback(async (firebaseUser, forceRefresh = false) => {
    const tokenResult = await firebaseUser.getIdTokenResult(forceRefresh);
    const isPro = !!tokenResult.claims.isPro;
    const proUntil = tokenResult.claims.proUntil; // Unix timestamp in seconds
    const now = Math.floor(Date.now() / 1000);
    const isProActive = isPro && proUntil && (now < proUntil);

    return {
      ...firebaseUser,
      isPro: isProActive,
      proUntil: proUntil
    };
  }, []);

  const refreshUser = useCallback(async () => {
    if (!auth.currentUser) return null;
    for (let i = 0; i < 5; i++) {
      const userWithClaims = await getUserWithClaims(auth.currentUser, true);

      if (userWithClaims.isPro) {
        console.log("Pro claim detected!");
        setUser(userWithClaims);
        return userWithClaims;
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    const finalUser = await getUserWithClaims(auth.currentUser);
    setUser(finalUser);
    return finalUser;
  }, [getUserWithClaims]);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userWithClaims = await getUserWithClaims(firebaseUser);
          setUser(userWithClaims);
        } catch (err) {
          console.error("Error getting token claims:", err);
          setUser(firebaseUser);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    return signInWithEmailAndPassword(auth, email, password);
  }, []);

  const register = useCallback(async (email, password) => {
    setError(null);
    return createUserWithEmailAndPassword(auth, email, password);
  }, []);

  const logout = useCallback(async () => {
    setError(null);
    setUser(null);
    return signOut(auth);
  }, [user]);

  const processProPurchase = useCallback(async (orderId) => {
    const processPurchaseFn = httpsCallable(functions, 'processPaypalProPurchase');
    try {
      const result = await processPurchaseFn({ orderId });
      if (result.data.success) {
        await refreshUser();
      }
      return result.data;
    } catch (err) {
      throw err;
    }
  }, [refreshUser]);

  const value = { user, loading, error, login, register, logout, setError, refreshUser, processProPurchase };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
